/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useForm, zodResolver } from "@mantine/form";
import {
  Grid,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  Checkbox,
  Button,
  Group,
  Text,
  ScrollArea,
  Chip,
  ActionIcon,
  Box,
  Loader,
  Paper,
  MultiSelect,
} from "@mantine/core";
import { z } from "zod";
import { Check, ExclamationMark } from "tabler-icons-react";
import { showNotification } from "@mantine/notifications";
import {
  IconPlus,
  IconCalendarEvent,
  IconTrash,
  IconX,
} from "@tabler/icons-react";

import { useDebouncedValue } from "@mantine/hooks";
import { useSearchOrgUsers } from "api/chat/userSearch";
import { useSaveMeeting } from "api/meeting/updateMeeting";
import { useGetMeeting } from "api/meeting/getMeetingById";

export type Attendee = {
  user: string;
  userType: string;
  displayName?: string;
  status?: "pending" | "accepted" | "declined";
};

export type NewMeetingInput = {
  title: string;
  description: string;
  scheduledDate: Date;
  duration: number;
  meetingPlatform: string;
  meetingLink: string;
  attendees: Attendee[];
  agenda: string;
  projectId: string;
  isRecurring?: boolean;
  recurringPattern?: {
    frequency?: "daily" | "weekly" | "monthly";
    interval?: number;
    endDate?: Date;
    daysOfWeek?: number[];
  };
};

const meetingSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  scheduledDate: z.date(),
  duration: z.number().min(1, "Duration must be at least 1 minute"),
  meetingPlatform: z.string().min(1, "Meeting platform is required"),
  meetingLink: z.string().min(1, "Meeting link is required"),
  attendees: z
    .array(
      z.object({
        user: z.string().min(1, "User ID is required"),
        userType: z.string().min(1),
        displayName: z.string().optional(),
        status: z.enum(["pending", "accepted", "declined"]).optional(),
      })
    )
    .min(1, "At least one attendee is required"),
  agenda: z.string().min(1, "Agenda is required"),
  projectId: z.string().min(1, "Project ID is required"),
  isRecurring: z.boolean().optional(),
  recurringPattern: z
    .object({
      frequency: z.enum(["daily", "weekly", "monthly"]).optional(),
      interval: z.number().min(1).optional(),
      endDate: z.date().optional(),
      daysOfWeek: z.array(z.number().min(0).max(6)).optional(),
    })
    .optional(),
});

type MeetingFormProps = {
  initialValues?: Partial<NewMeetingInput>;
  onClose?: () => void;
  onReset?: () => void;
  submitLabel?: string;
  currentMeetingId?: string;
};

type OrgUser = {
  id: string;
  name?: string;
  email?: string;
};

export default function UpdateMeeting({
  initialValues = {},
  onClose,
  onReset,
  currentMeetingId,
  submitLabel = "Update Meeting",
}: MeetingFormProps) {
  const { data, isLoading, isError } = useGetMeeting(currentMeetingId);
  const { mutate: saveMeetingMutate } = useSaveMeeting();
  const [q, setQ] = useState("");
  const [debouncedQ] = useDebouncedValue(q, 300);

  const { data: rawUsers, isLoading: usersLoading } = useSearchOrgUsers(
    debouncedQ,
    10
  );

  const users: OrgUser[] = useMemo(() => {
    if (!rawUsers || !Array.isArray(rawUsers)) return [];
    return (rawUsers as any[])
      .map((u) => {
        const id = u.id ?? u._id ?? u.userId ?? u.user ?? (u as any).uid;
        const name =
          u.name ??
          u.fullName ??
          (`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || undefined);
        const email = u.email ?? u.emailAddress ?? undefined;
        return { id, name, email };
      })
      .filter((x) => !!x.id);
  }, [rawUsers]);

  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null);

  const defaultScheduled =
    initialValues.scheduledDate instanceof Date
      ? initialValues.scheduledDate
      : initialValues.scheduledDate
      ? new Date(initialValues.scheduledDate as any)
      : new Date();

  const form = useForm<NewMeetingInput>({
    initialValues: {
      title: initialValues.title ?? "",
      duration: initialValues.duration ?? 15,
      scheduledDate: defaultScheduled,
      meetingPlatform: initialValues.meetingPlatform ?? "",
      description: initialValues.description ?? "",
      meetingLink: initialValues.meetingLink ?? "",
      agenda: initialValues.agenda ?? "",
      projectId: initialValues.projectId ?? "",
      isRecurring: initialValues.isRecurring ?? false,
      attendees: initialValues.attendees ?? [],
      recurringPattern: {
        frequency:
          (initialValues.recurringPattern?.frequency as
            | "daily"
            | "weekly"
            | "monthly"
            | undefined) ?? undefined,
        interval: initialValues.recurringPattern?.interval ?? 1,
        endDate: initialValues.recurringPattern?.endDate
          ? new Date(initialValues.recurringPattern.endDate as any)
          : undefined,
        daysOfWeek: initialValues.recurringPattern?.daysOfWeek ?? [],
      },
    },
    validate: zodResolver(meetingSchema),
  });

  const [attendeeType, setAttendeeType] = React.useState<string>("User");

  useEffect(() => {
    if (!selectedUser) return;
    setQ(selectedUser.name ?? selectedUser.email ?? selectedUser.id);
  }, [selectedUser]);

  useEffect(() => {
    if (!data || !data.data) return;

    if (form.isDirty()) return;

    const d: any = data.data;
    const scheduledDate = d.scheduledDate
      ? new Date(d.scheduledDate)
      : new Date();

    const attendees: Attendee[] = (d.attendees ?? []).map((a: any) => {
      let userId: string | undefined;
      let displayName: string | undefined;
      if (a.user) {
        if (typeof a.user === "string") {
          userId = a.user;
        } else if (typeof a.user === "object") {
          userId = a.user._id ?? a.user.id ?? undefined;
          displayName =
            a.user.firstName || a.user.lastName
              ? `${a.user.firstName ?? ""} ${a.user.lastName ?? ""}`.trim()
              : a.user.email ?? undefined;
        }
      } else if (a.userId) {
        userId = a.userId;
      }

      return {
        user: userId ?? String(a._id ?? a.user ?? ""),
        userType: a.userType ?? "User",
        displayName: a.displayName ?? displayName,
        status: a.status ?? "pending",
      } as Attendee;
    });

    let projectId: string | undefined = "";
    if (d.projectId) {
      projectId =
        typeof d.projectId === "string"
          ? d.projectId
          : d.projectId._id ?? d.projectId.id ?? "";
    }

    const rpRaw = d.recurringPattern ?? {};
    const recurringPattern = {
      frequency: rpRaw.frequency ?? undefined,
      interval: rpRaw.interval ?? 1,
      endDate: rpRaw.endDate ? new Date(rpRaw.endDate) : undefined,
      daysOfWeek: Array.isArray(rpRaw.daysOfWeek) ? rpRaw.daysOfWeek : [],
    };

    form.setValues({
      title: d.title ?? "",
      description: d.description ?? "",
      scheduledDate,
      duration: d.duration ?? 15,
      meetingPlatform: d.meetingPlatform ?? "",
      meetingLink: d.meetingLink ?? "",
      agenda: d.agenda ?? "",
      projectId: projectId ?? "",
      isRecurring: !!d.isRecurring,
      attendees,
      recurringPattern,
    });
  }, [data]);

  function toInputValue(d: Date) {
    const pad = (n: number) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  function handleReset(): void {
    form.reset();
    setQ("");
    setSelectedUser(null);
    setAttendeeType("User");
    if (onReset) onReset();
  }

  const addAttendee = (e?: React.FormEvent) => {
    e?.preventDefault();

    let userId: string | undefined;
    let displayName: string | undefined;

    if (selectedUser) {
      userId = selectedUser.id;
      displayName = selectedUser.name ?? selectedUser.email ?? selectedUser.id;
    } else {
      const raw = q.trim();
      if (!raw) return;
      userId = raw;
      displayName = raw;
    }

    const existing = (form.values.attendees ?? []).some(
      (a) => a.user === userId
    );
    if (existing) {
      setQ("");
      setSelectedUser(null);
      return;
    }

    const next: Attendee[] = [
      ...(form.values.attendees ?? []),
      {
        user: userId!,
        userType: attendeeType,
        displayName,
        status: "pending" as const,
      },
    ];

    form.setFieldValue("attendees", next);

    setQ("");
    setSelectedUser(null);
  };

  const removeAttendee = (userId: string) => {
    form.setFieldValue(
      "attendees",
      (form.values.attendees ?? []).filter((a) => a.user !== userId)
    );
  };

  function buildApiPayload(values: NewMeetingInput) {
    const base: any = {
      title: values.title?.trim(),
      description: values.description?.trim() || undefined,
      scheduledDate: values.scheduledDate.toISOString(),
      duration: values.duration,
      meetingPlatform: values.meetingPlatform ?? undefined,
      meetingLink: values.meetingLink?.trim() || undefined,
      attendees: (values.attendees ?? []).map((a) => ({
        user: a.user,
        userType: a.userType,
      })),
      agenda: values.agenda?.trim() || undefined,
      projectId: values.projectId?.trim() || undefined,
      isRecurring: !!values.isRecurring,
    };

    if (values.isRecurring && values.recurringPattern) {
      const rp = values.recurringPattern;
      const recurringPattern: any = {
        frequency: rp.frequency,
        interval: rp.interval ?? 1,
      };
      if (rp.endDate) recurringPattern.endDate = rp.endDate.toISOString();
      if (Array.isArray(rp.daysOfWeek) && rp.daysOfWeek.length > 0)
        recurringPattern.daysOfWeek = rp.daysOfWeek;

      base.recurringPattern = recurringPattern;
    }

    return base;
  }

  type FormValues = typeof form.values;

  const handleSubmit = (values: FormValues) => {
    const payload = buildApiPayload(values);

    const argForMutate: any = { meetingId: currentMeetingId, ...payload };

    saveMeetingMutate(argForMutate, {
      onSuccess: () => {
        handleReset();
        if (onClose) onClose();
        showNotification({
          title: "Success",
          message: "Meeting updated",
          color: "teal",
          icon: <Check />,
        });
      },
      onError: (err: any) => {
        showNotification({
          title: "Server Error",
          message: "Please try again later",
          color: "red",
          icon: <ExclamationMark />,
        });
      },
    });
  };

  const handleSelectUser = (u: OrgUser) => {
    setSelectedUser(u);
  };

  if (currentMeetingId && isLoading) {
    return (
      <Box style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <Loader />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box style={{ padding: 16 }}>
        <Text color="red">Failed to load meeting. Please try again.</Text>
      </Box>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Meeting Title"
            withAsterisk
            {...form.getInputProps("title")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            label="Duration (minutes)"
            withAsterisk
            min={1}
            {...form.getInputProps("duration")}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <Select
            label="Meeting Platform"
            withAsterisk
            data={[
              { value: "zoom", label: "Zoom" },
              { value: "google-meet", label: "Google Meet" },
              { value: "teams", label: "Microsoft Teams" },
              { value: "custom", label: "Custom" },
            ]}
            {...form.getInputProps("meetingPlatform")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label="Scheduled Date & Time"
            withAsterisk
            type="datetime-local"
            value={toInputValue(form.values.scheduledDate)}
            onChange={(e) =>
              form.setFieldValue(
                "scheduledDate",
                e.target.value ? new Date(e.target.value) : new Date()
              )
            }
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Project ID"
            withAsterisk
            {...form.getInputProps("projectId")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Meeting Link"
            withAsterisk
            {...form.getInputProps("meetingLink")}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Description"
            withAsterisk
            minRows={2}
            {...form.getInputProps("description")}
            autosize
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Agenda"
            withAsterisk
            minRows={2}
            {...form.getInputProps("agenda")}
            autosize
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={3}>
          <Checkbox
            {...form.getInputProps("isRecurring", { type: "checkbox" })}
            label="Recurring Meeting (optional)"
          />
        </Grid.Col>
      </Grid>

      {form.values.isRecurring && (
        <Grid mt={8}>
          <Grid.Col span={6}>
            <Select
              label="Frequency"
              data={[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
              ]}
              value={form.values.recurringPattern?.frequency ?? undefined}
              onChange={(v) =>
                form.setFieldValue("recurringPattern", {
                  ...(form.values.recurringPattern ?? {}),
                  frequency: v as "daily" | "weekly" | "monthly",
                })
              }
              clearable
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <NumberInput
              label="Interval"
              min={1}
              value={form.values.recurringPattern?.interval ?? 1}
              onChange={(val) =>
                form.setFieldValue("recurringPattern", {
                  ...(form.values.recurringPattern ?? {}),
                  interval: val ?? 1,
                })
              }
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <MultiSelect
              label="Days of week"
              placeholder="Select days"
              data={[
                { value: "0", label: "Sunday" },
                { value: "1", label: "Monday" },
                { value: "2", label: "Tuesday" },
                { value: "3", label: "Wednesday" },
                { value: "4", label: "Thursday" },
                { value: "5", label: "Friday" },
                { value: "6", label: "Saturday" },
              ]}
              value={(form.values.recurringPattern?.daysOfWeek ?? []).map(
                String
              )}
              onChange={(vals) =>
                form.setFieldValue("recurringPattern", {
                  ...(form.values.recurringPattern ?? {}),
                  daysOfWeek: vals.map((v) => Number(v)),
                })
              }
              clearable
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label="End Date (optional)"
              type="date"
              value={
                form.values.recurringPattern?.endDate
                  ? form.values.recurringPattern.endDate
                      .toISOString()
                      .slice(0, 10)
                  : ""
              }
              onChange={(e) =>
                form.setFieldValue("recurringPattern", {
                  ...(form.values.recurringPattern ?? {}),
                  endDate: e.target.value
                    ? new Date(e.target.value)
                    : undefined,
                })
              }
            />
          </Grid.Col>
        </Grid>
      )}

      <Grid>
        <Grid.Col span={3}></Grid.Col>

        <Grid.Col span={9}>
          <Box style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <Box style={{ flex: 1, position: "relative" }}>
              <TextInput
                placeholder="Search user..."
                value={q}
                onChange={(e) => {
                  setQ(e.currentTarget.value);
                  setSelectedUser(null);
                }}
                style={{ width: "100%" }}
                rightSection={
                  q ? (
                    <ActionIcon
                      aria-label="clear search"
                      onClick={() => {
                        setQ("");
                        setSelectedUser(null);
                      }}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  ) : undefined
                }
              />

              {q.trim() !== "" &&
                (usersLoading || (users && users.length > 0)) && (
                  <Paper
                    withBorder
                    shadow="sm"
                    style={{
                      position: "absolute",
                      zIndex: 50,
                      left: 0,
                      right: 0,
                      marginTop: 6,
                      maxHeight: 220,
                      overflow: "auto",
                    }}
                  >
                    {usersLoading ? (
                      <Box p="xs">
                        <Group spacing="xs">
                          <Loader size="xs" />
                          <Text size="xs">Searching...</Text>
                        </Group>
                      </Box>
                    ) : users && users.length > 0 ? (
                      <ScrollArea style={{ maxHeight: 220 }}>
                        <Box p="xs">
                          {users.map((u) => (
                            <Box
                              key={u.id}
                              sx={{
                                padding: "6px 8px",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => handleSelectUser(u)}
                            >
                              <Group noWrap position="apart" spacing="xs">
                                <div>
                                  <Text size="sm" weight={500}>
                                    {u.name ?? u.email ?? u.id}
                                  </Text>
                                  {u.email && (
                                    <Text size="xs" color="dimmed">
                                      {u.email}
                                    </Text>
                                  )}
                                </div>
                                <div>
                                  <Text size="xs" color="dimmed">
                                    Select
                                  </Text>
                                </div>
                              </Group>
                            </Box>
                          ))}
                        </Box>
                      </ScrollArea>
                    ) : (
                      <Box p="xs">
                        <Text size="xs" color="dimmed">
                          No users found
                        </Text>
                      </Box>
                    )}
                  </Paper>
                )}
            </Box>

            <Select
              data={[{ value: "User", label: "User" }]}
              value={attendeeType}
              onChange={(v) => setAttendeeType(v ?? "User")}
              style={{ minWidth: 120 }}
            />

            <Button
              onClick={(e: any) => {
                e.preventDefault();
                addAttendee();
              }}
              leftIcon={<IconPlus />}
            >
              Add
            </Button>
          </Box>
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={12}>
          <Text weight={700} mb="xs">
            Attendees
          </Text>
          <ScrollArea style={{ maxHeight: 120 }}>
            <Group spacing={8}>
              {(form.values.attendees ?? []).length === 0 && (
                <Text color="dimmed">No attendees added</Text>
              )}

              {(form.values.attendees ?? []).map((a) => (
                <Chip key={a.user}>
                  <Group spacing={8} noWrap align="center">
                    <Text size="sm">
                      {a.displayName ?? a.user} ({a.userType})
                    </Text>
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      onClick={() => removeAttendee(a.user)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Chip>
              ))}
            </Group>

            {form.errors.attendees && (
              <Text size="xs" color="red" mt="xs">
                {String(form.errors.attendees)}
              </Text>
            )}
          </ScrollArea>
        </Grid.Col>
      </Grid>

      <Group position="right" spacing={20} mt={10}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          disabled={!form.isDirty()}
          loading={isLoading}
          leftIcon={<IconCalendarEvent />}
        >
          Update Meeting
        </Button>
      </Group>
    </form>
  );
}
